import React, { useState, useMemo } from 'react';
import { NodeDevice } from '../types';

interface NodesTableProps {
    nodes: NodeDevice[];
    onDelete?: (id: string | number) => void;
    searchTerm: string;
}

const NodesTable: React.FC<NodesTableProps> = ({ nodes, onDelete, searchTerm }) => {
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    // Filtering logic
    const filteredNodes = useMemo(() => {
        if (!searchTerm) return nodes;
        const lowerSearch = searchTerm.toLowerCase();
        return nodes.filter(n => 
            n.name.toLowerCase().includes(lowerSearch) || 
            n.ip.toLowerCase().includes(lowerSearch)
        );
    }, [nodes, searchTerm]);

    // Grouping logic (MC01-01 -> MC01)
    const groupedNodes = useMemo(() => {
        const groups: Record<string, NodeDevice[]> = {};
        filteredNodes.forEach(node => {
            const prefix = node.name.includes('-') ? node.name.split('-')[0] : 'Otros';
            if (!groups[prefix]) groups[prefix] = [];
            groups[prefix].push(node);
        });
        return groups;
    }, [filteredNodes]);

    const toggleGroup = (prefix: string) => {
        setExpandedGroups(prev => ({ ...prev, [prefix]: !prev[prefix] }));
    };

    if (filteredNodes.length === 0 && searchTerm) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-[#161b22] border border-dashed border-[#30363d] rounded-3xl">
                <span className="material-icons-round text-gray-600 text-3xl mb-3">search_off</span>
                <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">No se encontraron resultados</p>
            </div>
        );
    }

    if (nodes.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24 bg-[#161b22] border border-dashed border-[#30363d] rounded-3xl">
                <div className="w-16 h-16 bg-[#0d1117] rounded-full flex items-center justify-center mb-4 border border-[#30363d]">
                    <span className="material-icons-round text-gray-600 text-3xl">inventory_2</span>
                </div>
                <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">Sin dispositivos</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {Object.entries(groupedNodes).map(([prefix, groupNodes]: [string, NodeDevice[]]) => {
                const isExpanded = expandedGroups[prefix] ?? (searchTerm ? true : false);
                const onlineCount = groupNodes.filter(n => n.status?.toUpperCase() === 'ONLINE').length;
                const totalCount = groupNodes.length;

                return (
                    <div key={prefix} className="bg-[#161b22] border border-[#30363d] rounded-2xl overflow-hidden transition-all duration-300 shadow-xl">
                        {/* Group Header */}
                        <div 
                            onClick={() => toggleGroup(prefix)}
                            className="flex items-center justify-between px-6 py-5 cursor-pointer hover:bg-[#1c2128] transition-colors select-none"
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${isExpanded ? 'bg-primary/20 border-primary/30 text-primary shadow-[0_0_15px_rgba(57,224,121,0.1)]' : 'bg-[#0d1117] border-[#30363d] text-gray-500'}`}>
                                    <span className="material-icons-round">{isExpanded ? 'folder_open' : 'folder'}</span>
                                </div>
                                <div>
                                    <h3 className="text-white font-black text-sm tracking-tighter uppercase">{prefix}</h3>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">
                                            {totalCount} Dispositivos
                                        </span>
                                        <span className="text-gray-700 text-[10px]">•</span>
                                        <span className="text-[9px] font-bold text-success uppercase">
                                            {onlineCount} Online
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                                <span className={`material-icons-round text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-primary' : ''}`}>
                                    expand_more
                                </span>
                            </div>
                        </div>

                        {/* Collapsible List Content */}
                        <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100 border-t border-[#30363d]/50' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-[#0d1117]/50 text-[9px] font-black text-gray-600 uppercase tracking-[0.2em]">
                                            <th className="px-6 py-3 w-16">ID</th>
                                            <th className="px-6 py-3">Nombre</th>
                                            <th className="px-6 py-3">IP / Puerto</th>
                                            <th className="px-6 py-3">CH</th>
                                            <th className="px-6 py-3">Hardware</th>
                                            <th className="px-6 py-3">Estado</th>
                                            <th className="px-6 py-3 text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#30363d]/20">
                                        {groupNodes.map((node) => {
                                            const isOnline = node.status?.toUpperCase() === 'ONLINE';
                                            return (
                                                <tr key={node.id} className="hover:bg-[#1c2128] transition-colors group">
                                                    <td className="px-6 py-3 text-[10px] font-mono text-gray-600">#{node.id}</td>
                                                    <td className="px-6 py-3">
                                                        <span className="text-xs font-bold text-white group-hover:text-primary transition-colors">{node.name}</span>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-mono text-gray-400 bg-[#0d1117] px-1.5 py-0.5 rounded border border-[#30363d]">{node.ip}</span>
                                                            <span className="text-[10px] font-mono text-blue-400">:{node.port}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <span className="text-[10px] font-black text-primary/70">{node.channel}</span>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <div className="flex flex-col">
                                                            <span className="text-[9px] font-bold text-gray-400 uppercase">{node.brand}</span>
                                                            <span className="text-[9px] text-gray-600 uppercase tracking-tighter">{node.type}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-success shadow-[0_0_8px_#10b981]' : 'bg-danger shadow-[0_0_8px_#f85149]'}`}></div>
                                                            <span className={`text-[9px] font-black uppercase ${isOnline ? 'text-success' : 'text-danger'}`}>
                                                                {node.status}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3 text-right">
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDelete?.(node.id);
                                                            }}
                                                            className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-danger/10 text-gray-500 hover:text-danger rounded-lg transition-all"
                                                        >
                                                            <span className="material-icons-round text-sm">delete_outline</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default NodesTable;